import { useEffect } from "react";

import { selectCurrentBuildingID } from "@/features/cartography/cartographySlice.selectors";
import { selectConfigItem } from "../config/configSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  EXPIRATION_CHECK_INTERVAL_MS,
  SharingStatus,
  selectExpirationTimestamp,
  selectReceivedSessionId,
  selectSharingStatus,
  setExpirationTimestamp,
  setSessionId,
  setSharedLocationStatus,
  setShareLocationModalOpen,
} from "@/features/share_location";
import { useSitumAPIContext } from "@/features/situm_api/SitumAPIProvider";
import { calculateRemainingMinutes, isExpired } from "@/features/utils/date";
import { WebviewApi } from "../webview_api";

export const useEmitLocationShare = () => {
  const dispatch = useAppDispatch();
  const sharingStatus = useAppSelector(selectSharingStatus);
  const expirationTimestamp = useAppSelector(selectExpirationTimestamp);
  const shareLocationFF = useAppSelector(
    selectConfigItem("feature_flags.share_location"),
  );
  const receivedSessionId = useAppSelector(selectReceivedSessionId);

  const { getActiveShareLocationSessionByDeviceId } = useSitumAPIContext();
  const deviceId = useAppSelector(selectConfigItem("internal.deviceId"));
  const domain = useAppSelector(selectConfigItem("auth.domain"));
  const apikey = useAppSelector(selectConfigItem("auth.api_key"));
  const currentBuildingID = useAppSelector(selectCurrentBuildingID);

  // Timer for active sharing session
  useEffect(() => {
    if (!shareLocationFF) return;
    let interval: NodeJS.Timeout;

    if (sharingStatus === SharingStatus.SHARING) {
      interval = setInterval(() => {
        if (isExpired(expirationTimestamp)) {
          // time expired
          WebviewApi.onShareLocationStopped();
          dispatch(setSharedLocationStatus(SharingStatus.COMPLETED));
          dispatch(setShareLocationModalOpen(true));
        }
      }, EXPIRATION_CHECK_INTERVAL_MS);
    }

    return () => clearInterval(interval);
  }, [shareLocationFF, sharingStatus, expirationTimestamp, dispatch]);

  // Check for existing active session on mount
  useEffect(() => {
    if (!shareLocationFF || !deviceId || receivedSessionId) return;

    getActiveShareLocationSessionByDeviceId({ deviceId })
      .then((res) => {
        if (res?.sessionId) {
          dispatch(setSessionId(res.sessionId));
          if (res.expirationTimestamp) {
            const expMs = new Date(res.expirationTimestamp).getTime();
            dispatch(setExpirationTimestamp(expMs));
            WebviewApi.onShareLocationStarted({
              durationMinutes: calculateRemainingMinutes(expMs),
              updateInterval: "realtime",
            });
          }
          dispatch(setSharedLocationStatus(SharingStatus.SHARING));
        }
      })
      .catch((err) => {
        console.error("Failed to fetch active share location session:", err);
      });
  }, [
    receivedSessionId,
    shareLocationFF,
    deviceId,
    getActiveShareLocationSessionByDeviceId,
    dispatch,
    domain,
    apikey,
    currentBuildingID,
  ]);
};
